import { alpha, Box, Divider, Stack, Text } from '@mantine/core';
import { useSafeLocalizedCompactCurrencyFormatter } from 'shared/utils/currency';

import { type PaidByReminderSegment, segmentKey } from './paid-by-reminder-segments';
import { formatPercentage } from './percentage';

const ROW_GRID = {
  gridTemplateColumns: '16px minmax(0, 1fr) 46px 46px 64px',
  columnGap: 'var(--mantine-spacing-space3)',
  alignItems: 'center',
  padding: '5px 8px',
  borderRadius: 6,
} as const;

const FIGURE = { fontVariantNumeric: 'tabular-nums' } as const;

const TITLE_TRACKING = { letterSpacing: '0.04em' } as const;

type PaidByReminderWorkflowTooltipProps = {
  workflowName: string;
  segments: ReadonlyArray<PaidByReminderSegment>;
  highlightedKey: string;
};

export const PaidByReminderWorkflowTooltip = ({
  workflowName,
  segments,
  highlightedKey,
}: PaidByReminderWorkflowTooltipProps) => {
  const formatAmount = useSafeLocalizedCompactCurrencyFormatter();

  const row = (segment: PaidByReminderSegment) => {
    const isHighlighted = segmentKey(segment) === highlightedKey;
    const weight = isHighlighted ? 600 : 400;

    return (
      <Box
        key={segmentKey(segment)}
        aria-current={isHighlighted || undefined}
        display="grid"
        style={{
          ...ROW_GRID,
          backgroundColor: isHighlighted ? alpha(segment.color, 0.12) : 'transparent',
        }}
      >
        <Box
          aria-hidden
          w={10}
          h={10}
          style={{ borderRadius: 3, backgroundColor: segment.color }}
        />
        <Text size="sm" lh={1.3} fw={weight} c={isHighlighted ? 'gray.9' : 'gray.7'}>
          {segment.label}
        </Text>
        <Text size="sm" fw={weight} ta="right" c="gray.9" style={FIGURE}>
          {formatPercentage(segment.share)}
        </Text>
        <Text size="sm" fw={weight} ta="right" c="gray.7" style={FIGURE}>
          {segment.count}
        </Text>
        <Text size="sm" fw={weight} ta="right" c="gray.9" style={FIGURE}>
          {formatAmount.format(segment.amount)}
        </Text>
      </Box>
    );
  };

  return (
    <Stack gap={8}>
      <Text fw={700} size="xs" c="gray.6" lh={1.3} tt="uppercase" style={TITLE_TRACKING}>
        {workflowName}
      </Text>
      <Divider color="gray.2" />
      <Stack gap={1}>{segments.map(row)}</Stack>
    </Stack>
  );
};
